import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  SwitchAccount as SwitchAccountIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  Laptop as LaptopIcon,
  OpenInNew
} from '@mui/icons-material';
import { User } from '../../../types';
import { UserSessionRow } from '../../../allUsersApi';

interface UserSessionsSectionProps {
  user: User;
  /** users.impersonate: сервер не выставляет флаг для себя и для равных и старших рангов */
  canImpersonate: boolean;
  /** Завершать сессии можно свои или при users.toggle_status в зоне оператора (как в authV2) */
  canRevokeSessions: boolean;
  displayName: string;
  /** Идет вход под сотрудником: кнопка заблокирована до перехода в его интерфейс */
  impersonating: boolean;
  onImpersonate: () => void;
  sessions: UserSessionRow[];
  loadingSessions: boolean;
  onRefreshSessions: () => void;
  revokingAll: boolean;
  onRevokeAllSessions: () => void;
  revokingSessionId: number | null;
  onRevokeSingleSession: (sessionId: number) => void;
}

export const UserSessionsSection: React.FC<UserSessionsSectionProps> = ({
  user,
  canImpersonate,
  canRevokeSessions,
  displayName,
  impersonating,
  onImpersonate,
  sessions,
  loadingSessions,
  onRefreshSessions,
  revokingAll,
  onRevokeAllSessions,
  revokingSessionId,
  onRevokeSingleSession
}) => {
  return (
    <Box id="user-section-sessions" sx={{ scrollMarginTop: '60px', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Карточка входа под пользователем (Режим проверки) */}
      {canImpersonate && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: '16px',
            bgcolor: '#f5f3ff',
            border: '1.5px solid #ddd6fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            boxShadow: '0 2px 10px rgba(109, 40, 217, 0.04)'
          }}
        >
          <Box sx={{ maxWidth: '650px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#5b21b6', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwitchAccountIcon sx={{ fontSize: 20 }} />
              Режим проверки интерфейса («Войти под пользователем»)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6d28d9', mt: 0.5 }}>
              Позволяет администратору открыть портал точно так, как его видит сотрудник <strong>{displayName}</strong>: с его реальными правами, кнопками кассы, отчетов, подчиненными аптеками и интерфейсом.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
            onClick={onImpersonate}
            disabled={impersonating}
            sx={{
              bgcolor: '#6d28d9',
              fontWeight: 700,
              borderRadius: '10px',
              textTransform: 'none',
              px: 2.5,
              py: 1,
              boxShadow: '0 4px 12px rgba(109, 40, 217, 0.3)',
              '&:hover': { bgcolor: '#5b21b6' }
            }}
          >
            {impersonating ? 'Вход...' : 'Войти в аккаунт сотрудника'}
          </Button>
        </Paper>
      )}

      {/* Верхняя информационная панель */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '16px',
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: user.isOnline ? '#10b981' : '#94a3b8',
              boxShadow: user.isOnline ? '0 0 8px #10b981' : 'none'
            }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {user.isOnline ? 'Пользователь сейчас Онлайн' : 'Пользователь сейчас Офлайн'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {user.isOnline
                ? `Текущий рабочий раздел: ${user.currentSection || 'Главная'}`
                : user.lastActivityAt
                  ? `Последний визит: ${new Date(user.lastActivityAt).toLocaleString('ru-RU')}`
                  : 'Данных о недавней активности нет'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={onRefreshSessions}
            disabled={loadingSessions}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Обновить
          </Button>

          {canRevokeSessions && (
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={revokingAll ? <CircularProgress size={16} /> : <BlockIcon sx={{ fontSize: 16 }} />}
              onClick={onRevokeAllSessions}
              disabled={revokingAll || sessions.length === 0}
              sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#e11d48' }}
            >
              {revokingAll ? 'Сброс...' : 'Сбросить все сессии'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Список сессий */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '16px',
          bgcolor: '#ffffff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
          Журнал устройств и входов ({sessions.length})
        </Typography>

        {loadingSessions ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : sessions.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            У пользователя пока нет зафиксированных сессий в базе данных
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sessions.map((sess) => (
              <Box
                key={sess.id}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: sess.isRevoked ? '#f1f5f9' : (sess.isOnline ? '#a7f3d0' : '#e2e8f0'),
                  bgcolor: sess.isRevoked ? '#f8fafc' : (sess.isOnline ? '#f0fdf4' : '#ffffff'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  opacity: sess.isRevoked ? 0.65 : 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: sess.isOnline ? '#dcfce7' : '#f1f5f9',
                      color: sess.isOnline ? '#15803d' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <LaptopIcon sx={{ fontSize: 22 }} />
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {sess.deviceInfo || 'Устройство'}
                      </Typography>
                      {sess.isOnline && (
                        <Chip
                          label="Онлайн"
                          size="small"
                          sx={{
                            bgcolor: '#10b981',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            height: 20
                          }}
                        />
                      )}
                      {sess.isRevoked && (
                        <Chip
                          label={sess.revokedReason === 'password_changed' ? 'Смена пароля' : 'Завершена'}
                          size="small"
                          sx={{
                            bgcolor: '#f1f5f9',
                            color: '#64748b',
                            fontWeight: 600,
                            fontSize: '0.68rem',
                            height: 20
                          }}
                        />
                      )}
                      {sess.impersonatedBy && (
                        <Chip
                          label="Вход админа"
                          size="small"
                          sx={{
                            bgcolor: '#fef3c7',
                            color: '#b45309',
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            height: 20
                          }}
                        />
                      )}
                    </Box>

                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.3 }}>
                      IP: <strong>{sess.ipAddress}</strong> • Раздел: <strong>{sess.currentSection || '/'}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Вход: {new Date(sess.createdAt).toLocaleString('ru-RU')} • Активность: {new Date(sess.lastActivityAt).toLocaleString('ru-RU')}
                    </Typography>
                  </Box>
                </Box>

                {!sess.isRevoked && canRevokeSessions && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={revokingSessionId === sess.id}
                    onClick={() => onRevokeSingleSession(sess.id)}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem' }}
                  >
                    {revokingSessionId === sess.id ? 'Сброс...' : 'Завершить'}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default UserSessionsSection;
